import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { infaqService, DonationCampaign } from '../../../services/infaqService';
import LoadingSpinner from '../../../components/LoadingSpinner';

function InfaqCampaignsPage() {
  const [campaigns, setCampaigns] = useState<DonationCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const data = await infaqService.getAllCampaigns();
      setCampaigns(data);
    } catch (err: any) {
      setError(err.message || 'Gagal memuat kempen');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Adakah anda pasti mahu memadam kempen ini?')) return;

    try {
      await infaqService.deleteCampaign(id);
      setCampaigns(campaigns.filter((c) => c.id !== id));
    } catch (err: any) {
      alert(err.message || 'Gagal memadam kempen');
    }
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await infaqService.updateCampaign(id, { status });
      setCampaigns(campaigns.map((c) => (c.id === id ? { ...c, status } : c)));
    } catch (err: any) {
      alert(err.message || 'Gagal mengemas kini status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Pengurusan Kempen Infaq</h1>
            <p className="text-gray-600">Urus semua kempen dermaan di sini</p>
          </div>
          <Link
            to="/admin/infaq/campaigns/new"
            className="px-6 py-3 bg-[#0D7330] text-white font-semibold rounded-lg hover:bg-[#0a5c26] transition-colors"
          >
            + Kempen Baru
          </Link>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Campaigns Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Kempen</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Dikumpul</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Sasaran</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Penderma</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Tindakan</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {campaigns.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      Tiada kempen lagi
                    </td>
                  </tr>
                ) : (
                  campaigns.map((campaign) => (
                    <tr key={campaign.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-4">
                          <img
                            src={campaign.featuredImage || '/demo-help.jpg'}
                            alt={campaign.title}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          <div>
                            <Link
                              to={`/infaq/campaign/${campaign.slug}`}
                              target="_blank"
                              className="text-lg font-semibold text-gray-900 hover:text-[#0D7330] block"
                            >
                              {campaign.title}
                            </Link>
                            <p className="text-sm text-gray-500 mt-1 line-clamp-1">{campaign.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={campaign.status}
                          onChange={(e) => handleStatusChange(campaign.id, e.target.value)}
                          className={`px-3 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${
                            campaign.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800'
                              : campaign.status === 'PAUSED'
                              ? 'bg-yellow-100 text-yellow-800'
                              : campaign.status === 'COMPLETED'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          <option value="ACTIVE">Aktif</option>
                          <option value="PAUSED">Dijedakan</option>
                          <option value="COMPLETED">Selesai</option>
                          <option value="CANCELLED">Dibatalkan</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 text-right font-semibold text-[#0D7330]">
                        {infaqService.formatCurrency(campaign.currentAmount)}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-700">
                        {infaqService.formatCurrency(campaign.targetAmount)}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-700">{campaign.donorCount}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <Link
                            to={`/admin/infaq/campaigns/${campaign.id}/edit`}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </Link>
                          <Link
                            to={`/admin/infaq/campaigns/${campaign.id}/allocate`}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Peruntukkan Dana"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </Link>
                          <button
                            onClick={() => handleDelete(campaign.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Padam"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InfaqCampaignsPage;
